import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    options: Object,
  };
  static targets = [
    "input",
    "tabs",
    "containers",
    "permissions",
    "abilities",
    "scopes",
  ];

  workingData = {};
  activeTab = 0;

  connect() {
    this.workingData = JSON.parse(this.inputTarget.value);
    this.buildPermissionButtons();
    this.buildAbilityButtons();
    this.buildScopeButtons();
    this.setActiveTab();
  }

  updateInput() {
    this.inputTarget.value = JSON.stringify(this.workingData)
  }

  switchTab(event) {
    this.activeTab = parseInt(event.currentTarget.dataset.tab);
    this.setActiveTab();
  }

  setActiveTab() {
    const activeClasses = ["bg-zinc-200", "hover:bg-zinc-300"];
    const inactiveClasses = ["bg-white", "hover:bg-zinc-100"];

    this.tabsTargets.forEach((e) => {
      e.classList.remove(...activeClasses);
      e.classList.add(...inactiveClasses);
    });
    this.containersTargets.forEach((e) => {
      e.classList.add("hidden");
    });

    this.tabsTargets[this.activeTab].classList.remove(...inactiveClasses);
    this.tabsTargets[this.activeTab].classList.add(...activeClasses);
    this.containersTargets[this.activeTab].classList.remove("hidden");
  }

  buildPermissionButtons() {
    var content = "";
    for (const model of Object.keys(this.optionsValue)) {
      content += `
        <div class="grid grid-cols-12 py-2 border-b border-gray-200">
          <div class="col-span-2 text-center place-self-center">${
            this.optionsValue[model].display_name
          }</div>
          <div class="col-span-10 flex flex-col gap-2">
            <div class="grid grid-cols-10 gap-2">
              <div class="place-self-center">Read</div>
              <div class="col-span-9 flex flex-row flex-wrap gap-2">
                ${Object.keys(this.optionsValue[model].readable_fields)
                  .map((field) => this.buildPermissionButton(this.optionsValue[model].readable_fields, model, field, "read"))
                  .join("")}
              </div>
            </div>
            <div class="grid grid-cols-10 gap-2">
              <div class="place-self-center">Write</div>
              <div class="col-span-9 flex flex-row flex-wrap gap-2">
                ${Object.keys(this.optionsValue[model].writable_fields)
                  .map((field) => this.buildPermissionButton(this.optionsValue[model].writable_fields, model, field, "write"))
                  .join("")}
              </div>
            </div>
          </div>
        </div>
      `;
    }
    this.permissionsTarget.innerHTML = content;
  }

  buildPermissionButton(options, model, field, action) {
    const enabled = this.workingData.permissions[model]?.[field]?.[action];
    var colors = "";
    var subtitle = "";
    switch (enabled) {
      case undefined:
        colors = "text-blue-500 border-blue-500 bg-blue-50 hover:bg-blue-100";
        subtitle = "Unset";
        break;
      case true:
        colors = "text-white border-green-600 bg-green-400 hover:bg-green-500";
        subtitle = "Allowed";
        break;
      case false:
        colors = "text-white border-red-600 bg-red-400 hover:bg-red-500";
        subtitle = "Disallowed";
        break;
    }

    return `<div>
      <button data-action="click->permissioning#togglePermissionButton" data-model="${model}" data-field="${field}" data-permission-action="${action}" class="px-2 py-1 rounded-xl border ${colors} flex flex-col text-nowrap items-center" type="button">
        ${options[field]}
        <span class="text-xs font-bold">${subtitle}</span>
      </button>
    </div>`;
  }

  togglePermissionButton(event) {
    const { model, field, permissionAction: action } = event.currentTarget.dataset;

    const currentValue = this.workingData.permissions[model]?.[field]?.[action];
    this.workingData.permissions[model] ||= {};
    this.workingData.permissions[model][field] ||= {};
    switch (currentValue) {
      case undefined: // was unset, so enable it
        this.workingData.permissions[model][field][action] = true;
        break;
      case true: // was enabled, so disable it
        this.workingData.permissions[model][field][action] = false;
        break;
      case false: // was disabled, so unset it
        this.workingData.permissions[model][field][action] = undefined;
        break;
    }
    this.updateInput();
    this.buildPermissionButtons();
  }

  buildAbilityButtons() {
    var content = "";
    for (const model of Object.keys(this.optionsValue)) {
      content += `
        <div class="grid grid-cols-12 py-2 border-b border-gray-200">
          <div class="col-span-2 text-center place-self-center">${
            this.optionsValue[model].display_name
          }</div>
          <div class="col-span-10 flex flex-col gap-2">
            <div class="flex flex-row flex-wrap gap-2">
              ${Object.keys(this.optionsValue[model].abilities)
                .map((ability) =>
                  this.buildAbilityButton(
                    this.optionsValue[model].abilities,
                    model,
                    ability
                  )
                )
                .join("")}
            </div>
          </div>
        </div>
      `;
    }
    this.abilitiesTarget.innerHTML = content;
  }

  buildAbilityButton(options, model, ability) {
    const enabled = this.workingData.abilities[model]?.[ability];
    var colors = "";
    var subtitle = "";
    switch (enabled) {
      case undefined:
        colors = "text-blue-500 border-blue-500 bg-blue-50 hover:bg-blue-100";
        subtitle = "Unset";
        break;
      case true:
        colors = "text-white border-green-600 bg-green-400 hover:bg-green-500";
        subtitle = "Allowed";
        break;
      case false:
        colors = "text-white border-red-600 bg-red-400 hover:bg-red-500";
        subtitle = "Disallowed";
        break;
    }

    return `<div>
      <button data-action="click->permissioning#toggleAbilityButton" data-model="${model}" data-ability="${ability}" class="px-2 py-1 rounded-xl border ${colors} flex flex-col text-nowrap items-center" type="button">
        ${options[ability]}
        <span class="text-xs font-bold">${subtitle}</span>
      </button>
    </div>`;
  }

  toggleAbilityButton(event) {
    const { model, ability } = event.currentTarget.dataset;

    const currentValue = this.workingData.abilities[model]?.[ability];
    this.workingData.abilities[model] ||= {};
    switch (currentValue) {
      case undefined: // was unset, so enable it
        this.workingData.abilities[model][ability] = true;
        break;
      case true: // was enabled, so disable it
        this.workingData.abilities[model][ability] = false;
        break;
      case false: // was disabled, so unset it
        this.workingData.abilities[model][ability] = undefined;
        break;
    }
    this.updateInput();
    this.buildAbilityButtons();
  }

  buildScopeButtons() {
    var content = "";
    for (const model of Object.keys(this.optionsValue)) {
      content += `
        <div class="grid grid-cols-12 py-2 border-b border-gray-200">
          <div class="col-span-2 text-center place-self-center">${
            this.optionsValue[model].display_name
          }</div>
          <div class="col-span-10 flex flex-col gap-2">
            <div class="flex flex-row flex-wrap gap-2">
              ${Object.keys(this.optionsValue[model].scopes)
                .map((scope) =>
                  this.buildScopeButton(
                    this.optionsValue[model].scopes,
                    model,
                    scope
                  )
                )
                .join("")}
            </div>
          </div>
        </div>
      `;
    }
    this.scopesTarget.innerHTML = content;
  }

  buildScopeButton(options, model, scope) {
    const currentScope = this.workingData.scopes[model];
    var colors = "";
    if (scope == currentScope) {
      colors = "text-white border-green-600 bg-green-400 hover:bg-green-500";
    } else {
      colors = "text-blue-500 border-blue-500 bg-blue-50 hover:bg-blue-100";
    }

    return `<div>
      <button data-action="click->permissioning#setScope" data-model="${model}" data-scope="${scope}" class="px-2 py-1 rounded-xl border ${colors} flex flex-col text-nowrap items-center" type="button">
        ${options[scope]}
      </button>
    </div>`;
  }

  setScope(event) {
    const { model, scope } = event.currentTarget.dataset;

    this.workingData.scopes[model] = scope;
    this.updateInput();
    this.buildScopeButtons();
  }
}
